import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import {
  Plus, FolderOpen, MessageSquare, FileText, Package, ArrowLeft, MoreHorizontal,
  Trash2, Pencil, Loader2, Shield, LogOut, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await api.listProjects();
      setProjects(data);
    } catch (e) {
      toast.error('Errore nel caricamento progetti');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formName.trim()) return;
    setCreating(true);
    try {
      const proj = await api.createProject({ name: formName.trim(), description: formDesc.trim() });
      setShowCreate(false);
      setFormName('');
      setFormDesc('');
      navigate(`/projects/${proj.id}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questo progetto e tutti i suoi dati?')) return;
    try {
      await api.deleteProject(id);
      toast.success('Progetto eliminato');
      loadProjects();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C]" data-testid="projects-page">
      {/* Top bar */}
      <div className="h-14 flex items-center px-6 border-b border-white/[0.06] bg-[#101013]">
        <Link to="/chat" className="flex items-center gap-2 text-white/50 hover:text-white transition-colors mr-6">
          <ArrowLeft size={18} />
          <span className="text-sm">Chat</span>
        </Link>
        <div className="flex items-center gap-2 flex-1">
          <FolderOpen size={18} className="text-white/60" />
          <span className="text-white font-semibold text-lg">
            <span className="font-medium">TO</span><span className="font-extrabold">YOU</span>
            <span className="text-white/40 font-normal ml-2 text-sm">Progetti</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'admin' && (
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-white/40 hover:text-white hover:bg-white/[0.06]">
              <Shield size={14} className="mr-1" /> Admin
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors">
                <div className="h-7 w-7 rounded-full bg-white/10 flex items-center justify-center text-white text-xs font-semibold">
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </div>
                <ChevronDown size={12} className="text-white/30" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#141418] border-white/[0.08]">
              <DropdownMenuItem onClick={logout} className="text-red-400 focus:bg-red-400/10 focus:text-red-400 cursor-pointer">
                <LogOut size={14} className="mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">I tuoi progetti</h1>
            <p className="text-sm text-white/40 mt-1">Organizza il tuo lavoro in progetti con knowledge base e artifact</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-white text-black hover:bg-white/90" data-testid="create-project-button">
            <Plus size={16} className="mr-2" /> Nuovo progetto
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-white/40" size={24} />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen size={48} className="mx-auto text-white/10 mb-4" />
            <h3 className="text-lg text-white/50 mb-2">Nessun progetto</h3>
            <p className="text-sm text-white/30 mb-6">Crea il tuo primo progetto per organizzare chat, documenti e codice</p>
            <Button onClick={() => setShowCreate(true)} className="bg-white text-black hover:bg-white/90">
              <Plus size={16} className="mr-2" /> Crea progetto
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map(p => (
              <Card 
                key={p.id}
                className="bg-[#101013] border-white/[0.08] hover:border-white/[0.14] transition-colors cursor-pointer group"
                onClick={() => navigate(`/projects/${p.id}`)}
                data-testid="project-card"
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="h-10 w-10 rounded-lg bg-white/[0.06] flex items-center justify-center">
                      <FolderOpen size={18} className="text-white/40" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <button className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-white/60 transition-opacity p-1">
                          <MoreHorizontal size={14} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-[#141418] border-white/[0.08]" align="end">
                        <DropdownMenuItem 
                          className="text-red-400 focus:bg-red-400/10 focus:text-red-400 cursor-pointer text-xs"
                          onClick={(e) => handleDelete(p.id, e)}
                        >
                          <Trash2 size={12} className="mr-2" /> Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h3 className="text-white font-semibold mb-1 truncate">{p.name}</h3>
                  {p.description && (
                    <p className="text-white/40 text-sm mb-3 line-clamp-2">{p.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-xs text-white/30">
                    <span className="flex items-center gap-1"><MessageSquare size={12} /> {p.conversation_count || 0}</span>
                    <span className="flex items-center gap-1"><FileText size={12} /> {p.kb_file_count || 0}</span>
                    <span className="flex items-center gap-1"><Package size={12} /> {p.artifact_count || 0}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#141418] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Nuovo Progetto</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/60">Nome progetto</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)}
                placeholder="Es. App E-commerce" className="bg-white/[0.04] border-white/[0.08] text-white"
                data-testid="project-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60">Descrizione (opzionale)</Label>
              <Textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)}
                placeholder="Descrivi il progetto..." className="bg-white/[0.04] border-white/[0.08] text-white min-h-[80px]"
                data-testid="project-description-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/10 text-white/60 hover:bg-white/[0.04]">
              Annulla
            </Button>
            <Button onClick={handleCreate} disabled={creating || !formName.trim()} className="bg-white text-black hover:bg-white/90" data-testid="create-project-submit">
              {creating ? <Loader2 size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
              Crea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
